import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck2, RefreshCw, Unplug, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";

export const GoogleCalendarCard = () => {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const { data: st } = useQuery({ queryKey: ["gcal-status"], queryFn: () => api.get("/admin/gcal/status").then((r) => r.data) });

  useEffect(() => {
    const g = params.get("gcal");
    if (!g) return;
    if (g === "connected") toast.success("Google Calendar connected — syncing your paid bookings");
    else toast.error(g === "denied" ? "Google access was not granted"
      : g === "scope" ? "Calendar permission was not granted — click Connect again and TICK the box that lets XMD edit your calendar events."
      : "Could not connect Google Calendar", { duration: 10000 });
    params.delete("gcal");
    setParams(params, { replace: true });
    qc.invalidateQueries({ queryKey: ["gcal-status"] });
  }, [params, setParams, qc]);

  if (!st) return null;
  const run = async (fn, ok) => {
    if (busy) return;
    setBusy(true);
    try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r.data) : ok); qc.invalidateQueries({ queryKey: ["gcal-status"] }); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const connect = () => run(() => api.get("/admin/gcal/connect", { params: { origin: window.location.origin } })
    .then((r) => { window.location.href = r.data.authorization_url; return r; }), "Redirecting to Google…");

  return (
    <div className="bg-[#111] border border-white/10 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-4" data-testid="gcal-card">
      <CalendarCheck2 className={st.connected ? "text-green-400" : "text-white/40"} size={22} />
      <div className="flex-1 min-w-[200px]">
        <p className="text-white text-sm font-medium">Google Calendar sync</p>
        <p className="text-white/50 text-xs" data-testid="gcal-status-text">
          {!st.configured ? "Not set up yet — Google OAuth keys need to be added."
            : st.connected ? `Connected to ${st.email || "your Google account"} · paid bookings sync automatically`
            : "Not connected"}
        </p>
        {st.last_error && <p className="text-orange-400 text-xs mt-1 flex items-center gap-1" data-testid="gcal-error"><AlertTriangle size={12} /> {st.last_error}</p>}
      </div>
      {st.can_manage && st.configured && !st.connected && (
        <button onClick={connect} data-testid="gcal-connect-btn" className="btn-gold rounded-full px-5 py-2 text-xs font-mono-label uppercase">Connect Google Calendar</button>
      )}
      {st.can_manage && st.connected && (
        <div className="flex gap-2">
          <button onClick={() => run(() => api.post("/admin/gcal/sync"), (d) => `Synced ${d.synced} of ${d.total} upcoming paid booking${d.total === 1 ? "" : "s"}`)} data-testid="gcal-sync-btn" disabled={busy}
            className="disabled:opacity-50 rounded-full px-4 py-2 text-xs border border-white/20 text-white/80 hover:border-[#D4AF37] flex items-center gap-1.5"><RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Syncing…" : "Sync now"}</button>
          <button onClick={() => window.confirm("Disconnect Google Calendar? Existing events stay in Google.") && run(() => api.post("/admin/gcal/disconnect"), "Disconnected")} data-testid="gcal-disconnect-btn"
            className="rounded-full px-4 py-2 text-xs border border-white/10 text-white/50 hover:text-red-400 flex items-center gap-1.5"><Unplug size={13} /> Disconnect</button>
        </div>
      )}
    </div>
  );
};
